import { inject } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { CanDeactivateFn } from '@angular/router';
import { DialogService } from '@services/dialog.service';
import { isEqual } from 'lodash';
import {
    combineLatest,
    debounceTime,
    defer,
    distinctUntilChanged,
    finalize,
    fromEvent,
    map,
    merge,
    Observable,
    of,
    shareReplay,
    Subscription,
    switchMap,
    withLatestFrom,
} from 'rxjs';

function getControlValue(control: AbstractControl, withDisabled: boolean) {
    if (withDisabled && (control instanceof FormGroup || control instanceof FormArray)) {
        return control.getRawValue();
    }
    return control.value;
}

/**
 * Dirty check for form group
 *
 * @param source
 * @param control
 * @param withDisabled
 * @param debounce
 */
export function dirtyCheckFormGroup<S>(
    source: S,
    control: FormGroup,
    withDisabled = false,
    debounce = 300
) {
    const subscriptions: Subscription[] = [];
    const value = () => getControlValue(control, withDisabled);
    const valueChanges$ = merge(
        defer(() => of(value())),
        control.valueChanges.pipe(
            debounceTime(debounce),
            distinctUntilChanged(),
            map(() => value())
        )
    );

    const isDirty$ = combineLatest([defer(() => of(source)), valueChanges$]).pipe(
        map(([s, value]) => !isEqual(s, value)),
        finalize(() => subscriptions.forEach((s) => s.unsubscribe())),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    subscriptions.push(
        fromEvent(window, 'beforeunload')
            .pipe(withLatestFrom(isDirty$))
            .subscribe(([event, isDirty]) => {
                if (isDirty) {
                    event.preventDefault();
                    event.returnValue = false;
                }
            })
    );

    return isDirty$;
}

/**
 *  Dirty check for object
 *
 * @param source
 * @param valueChanges
 */
export function dirtyCheckObject<S>(source: S, valueChanges: Observable<S>) {
    const subscriptions: Subscription[] = [];
    const isDirty$ = combineLatest([defer(() => of(source)), valueChanges]).pipe(
        debounceTime(300),
        map(([s, value]) => !isEqual(s, value)),
        finalize(() => subscriptions.forEach((s) => s.unsubscribe())),
        // startWith(false),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    subscriptions.push(
        fromEvent(window, 'beforeunload')
            .pipe(withLatestFrom(isDirty$))
            .subscribe(([event, isDirty]) => {
                if (isDirty) {
                    event.preventDefault();
                    event.returnValue = false;
                }
            })
    );

    return isDirty$;
}

interface DirtyComponent {
    isDirty$: Observable<boolean>;
    isFinalEdit: boolean | (() => boolean);
    // isDirty$: Observable<boolean> | boolean | (() => boolean);
}

/**
 * Dirty check guard
 *
 * @param component
 */
export const dirtyCheckGuard: CanDeactivateFn<DirtyComponent> = (component) => {
    const componentDirty = component.isDirty$;
    const isFinalEdit = component.isFinalEdit;
    const hasFinalEdit =
        'isFinalEdit' in component &&
        ((typeof isFinalEdit === 'function' && isFinalEdit()) || isFinalEdit);

    if (!componentDirty || hasFinalEdit) {
        return of(true);
    }

    const dialogService = inject(DialogService);

    return componentDirty.pipe(
        switchMap((dirty) => {
            if (dirty) {
                return dialogService
                    .confirm({
                        title: $localize`:@@common.labels.warning:Cảnh báo`,
                        message: $localize`:@@common.labels.dirt-check.message:Bạn có thay đổi chưa được lưu. Bạn có thực sự muốn rời đi?`,
                        confirmText: $localize`:@@common.buttons.leave:Rời đi`,
                        cancelText: $localize`:@@common.buttons.stay:Ở lại`,
                    })
                    .pipe(map((result) => !!result));
            }
            return of(true);
        })
    );
};
